/**
 * A sortable column header: a button in the `<th>`, the arrow of the active
 * sort and `aria-sort`.
 */
import type { CSSProperties, ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons';
import { ariaSort } from './aria-sort';

export interface SortableHeaderProps {
  label: ReactNode;
  /** The column is the current sort key. */
  active: boolean;
  /** Drives `aria-sort`; the arrow points up when descending unless `arrow` says otherwise. */
  descending: boolean;
  arrow?: 'up' | 'down' | undefined;
  onClick: () => void;
  width?: string | number | undefined;
  style?: CSSProperties | undefined;
  className?: string | undefined;
  scope?: 'col' | undefined;
  /** Rendered after the button (a quick-search input, for instance). */
  children?: ReactNode;
  testId?: string | undefined;
}

export function SortableHeader({
  label,
  active,
  descending,
  arrow = descending ? 'up' : 'down',
  onClick,
  width,
  style,
  className,
  scope,
  children,
  testId,
}: Readonly<SortableHeaderProps>) {
  return (
    <th
      style={{ width, ...style }}
      className={className}
      scope={scope}
      aria-sort={ariaSort(active, descending)}
    >
      <button
        type="button"
        className="cursor-pointer"
        onClick={onClick}
        data-testid={testId}
      >
        {label}{' '}
        {active && (
          <FontAwesomeIcon icon={arrow === 'up' ? faArrowUp : faArrowDown} />
        )}
      </button>
      {children}
    </th>
  );
}
